import React, { useState } from 'react';
import apiService from '../services/api.service';
import { Box, Typography, TextField, Button, Card, CardContent, List, ListItem, ListItemText, Checkbox, Alert } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

interface DuplicateLead {
  id: number;
  phone: string;
  customer_name: string;
  address?: string;
  comment?: string;
}

export const DuplicatesPage: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicateLead[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const searchDuplicates = async () => {
    if (!phone.trim()) {
      setNotification('Введите номер телефона для поиска');
      return;
    }

    setLoading(true);
    try {
      const res = await apiService.get(`/duplicates/search?phone=${phone}`);
      setDuplicates(res.data);
      if (res.data.length > 1) {
        setNotification('Найдены дубликаты!');
      } else if (res.data.length === 1) {
        setNotification('Дубликаты не найдены.');
      } else {
        setNotification('Лиды с таким номером не найдены.');
      }
    } catch (error) {
      setNotification('Ошибка при поиске дубликатов');
    } finally {
      setLoading(false);
    }
  };

  const mergeDuplicates = async () => {
    if (selectedIds.length < 2) {
      setNotification('Выберите минимум 2 лида для объединения');
      return;
    }

    try {
      await apiService.post(`/duplicates/merge`, { leadIds: selectedIds });
      setNotification('Дубликаты объединены!');
      setDuplicates([]);
      setSelectedIds([]);
      fetchStats();
    } catch (error) {
      setNotification('Ошибка при объединении дубликатов');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await apiService.get(`/duplicates/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Ошибка при загрузке статистики:', error);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === duplicates.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(duplicates.map(lead => lead.id));
    }
  };

  const handleSelectLead = (leadId: number) => {
    setSelectedIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  React.useEffect(() => {
    fetchStats();
  }, []);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
        Управление дубликатами
      </Typography>

      {notification && (
        <Alert 
          severity={notification.includes('Ошибка') ? 'error' : 'info'} 
          onClose={() => setNotification(null)}
          sx={{ mb: 2 }}
        >
          {notification}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Поиск дубликатов
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexDirection: isMobile ? 'column' : 'row' }}>
            <TextField
              label="Номер телефона"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Введите номер телефона..."
              size="small"
              fullWidth={isMobile}
              sx={{ minWidth: isMobile ? 'auto' : 300 }}
            />
            <Stack direction="row" spacing={2}>
              <Button 
                variant="contained" 
                onClick={searchDuplicates}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                {loading ? '...' : 'Поиск'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={fetchStats}
                sx={{ flex: 1 }}
              >
                Обновить
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {stats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Статистика
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="white">
                  {stats.phones_with_duplicates || 0}
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  Телефонов
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                <Typography variant={isMobile ? "h5" : "h4"} color="white">
                  {stats.total_duplicates || 0}
                </Typography>
                <Typography variant="caption" color="white" display="block">
                  Дубликатов
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {duplicates.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', mb: 2, flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
              <Typography variant="h6">
                Найдено ({duplicates.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAll}
                  fullWidth={isMobile}
                >
                  {selectedIds.length === duplicates.length ? 'Снять' : 'Все'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={mergeDuplicates}
                  disabled={selectedIds.length < 2}
                  fullWidth={isMobile}
                >
                  Объединить ({selectedIds.length})
                </Button>
              </Box>
            </Box>

            <List sx={{ p: 0 }}>
              {duplicates.map((lead) => (
                <ListItem 
                  key={lead.id}
                  sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    mb: 1,
                    p: 1,
                    bgcolor: selectedIds.includes(lead.id) ? 'action.selected' : 'background.paper'
                  }}
                >
                  <Checkbox
                    checked={selectedIds.includes(lead.id)}
                    onChange={() => handleSelectLead(lead.id)}
                    size="small"
                  />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 0 : 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {lead.customer_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lead.phone}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {lead.address && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Адрес: {lead.address}
                          </Typography>
                        )}
                        {lead.comment && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Коммент: {lead.comment}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}; 